/**
 * ai/pii-scrubber (US-AI.F4-0233 · @UC9400 @SEC-FAIL-01) — CẠO PII TRƯỚC KHI RA KHỎI HỆ THỐNG.
 *
 * 🔴 BUG THẬT (@SEC-FAIL-01 · alias PII-EGRESS-RAW): câu chat của thủ quỹ ("Minh chuyển vào tk
 * 0987654321 rồi, gọi số 0901234567 xác nhận") được gửi NGUYÊN VĂN sang Anthropic/Google/OpenAI. Số tài
 * khoản, số điện thoại, email của người trong nhóm rời khỏi biên hệ thống và nằm trong log của bên thứ
 * ba — không thoả thuận, không xoá được, không ai biết.
 *
 * CÁCH LÀM: `scrubPiiForLlm(text)` chạy NGAY TRƯỚC lời gọi provider (một cổng duy nhất: LlmClient).
 *  - Câu THÔ vẫn lưu nguyên ở `ai_audit_log` (admin-only, có RBAC + redaction theo vai) ⇒ TRACE được.
 *  - Câu GỬI ĐI bị thay bằng placeholder ổn định `[SĐT]`, `[STK]`, `[EMAIL]` ⇒ LLM vẫn hiểu NGỮ CẢNH
 *    ("chuyển vào [STK]") mà KHÔNG cầm số thật.
 *  - Số TIỀN ("500k", "200.000đ") KHÔNG bị cạo — đó không phải PII và engine cần nó (nhưng engine tính
 *    tiền server-authoritative, LLM chỉ tô câu chữ — DP-3).
 *
 * THUẦN: không I/O, không state. Tất định (cùng input → cùng output) ⇒ eval reproducible.
 *
 * TRUNG THỰC VỀ GIỚI HẠN: đây là SO KHỚP MẪU (regex), không phải bộ phát hiện PII vạn năng. Nó chặn
 * các dạng phổ biến ở VN: email (kể cả "＠" toàn khổ) · SĐT (0/84/+84/(+84)/+84 (0), mã vùng trong ngoặc "(028)", cách/chấm/gạch xen giữa,
 * "_"/"/" khi cả số dùng một dấu) · STK/SĐT cách TỪNG chữ số (≥6) · STK liền ≥8 số
 * hoặc viết THEO NHÓM (mọi dấu cách Unicode/gạch/chấm; "/" khi có ngữ cảnh — PII-GROUPED-ACCOUNT 15/10 + PII-SCRUB-ROUND-3, luật ở
 * `classifyCluster` / `isSlashAccount`).
 * LỖ CÒN LẠI (biết, chưa chặn — mỗi lỗ có ca "lỗ đã biết" khẳng định hành vi hiện tại ở `pii-scrubber.test.ts`):
 *  - STK KHÔNG ngữ cảnh mạnh dưới 9 chữ số khi viết nhóm / dưới 8 khi viết liền — kể cả sau "tk"/"tài khoản"/tên ngân hàng
 *    (review #1411 HIGH: "chuyển tk 2500000" là TIỀN; không phân biệt được với số tiền "150000");
 *  - STK viết đúng dạng NGHÌN mà không có "stk/số tk/số tài khoản/a/c" đứng trước (trùng dạng tiền — giữ là tiền): bằng CHẤM/PHẨY ở mọi độ
 *    dài ("vcb 190.345.678.901"); bằng DẤU CÁCH khi dưới 9 chữ số sau tk/ngân hàng, dưới 10 chữ số không ngữ cảnh ("chuyển 123 456 789");
 *  - STK nối bằng "/" KHÔNG ngữ cảnh ("1903/4567/8901/23" — giữ để không bắt oan ngày/phân số), hoặc ≤8 chữ số sau tk/ngân hàng;
 *  - STK chia qua XUỐNG DÒNG hoặc trộn dấu nối ("stk 1903\n4567 8901 23" ⇒ "1903" lộ — ngữ cảnh dừng ở dấu ngắt mệnh đề);
 *  - ngữ cảnh HẸP: từ lạ chen giữa "số tk" và cụm ("số tk mới đã đổi thành 1903 4567" ⇒ lộ nguyên);
 *  - email viết lách ("a [at] gmail [dot] com"); số viết bằng CHỮ; SĐT trộn hai loại dấu rồi đứng sát một số khác;
 *  - "84.000.000.000" (tiền dạng nghìn mở bằng "84", 11 chữ số) bị nhận là SĐT ⇒ [SĐT] (có từ trước PII-GROUPED-ACCOUNT);
 *  - (AI-HARDEN A5) STK TRỘN chữ số lẻ với nhóm ("stk 1 9 0 3 4567 8901" ⇒ "1 9 0" lộ) · email có dấu chấm ý tự "。" ("a@gmail。com" — NFKC
 *    không đổi "。"; chuẩn hoá nó là đổi câu tiếng Trung/Nhật gửi LLM).
 * GIÁ ĐÃ CHẤP NHẬN (bắt OAN, không phải rò): tiền nghìn bằng dấu cách KHÔNG đơn vị ≥9 chữ số sau tk/ngân hàng ("chuyển tk 150 000 000") hoặc
 * ≥10 chữ số không ngữ cảnh ("quỹ có 1 200 000 000") ⇒ LLM thấy [STK] (PII-SCRUB-ROUND-3 — hằng `PII_SPACED_THOUSANDS_*`).
 * Tầng sau (audit + RBAC) vẫn là hàng rào.
 */

/** Placeholder ổn định (LLM giữ được ngữ cảnh mà không cầm số thật). */
export const PII_PLACEHOLDER = Object.freeze({
  PHONE: "[SĐT]",
  ACCOUNT: "[STK]",
  EMAIL: "[EMAIL]",
});

/** Loại PII bị cạo trong một lần scrub (audit/metric đếm được, KHÔNG lộ giá trị). */
export type PiiKind = "phone" | "account" | "email";

export interface ScrubResult {
  /** Văn bản AN TOÀN để gửi ra ngoài (LLM bên thứ ba). */
  readonly text: string;
  /** Có cạo gì không (caller có thể ghi metric/safety-event). */
  readonly redacted: boolean;
  /** Loại PII đã cạo (KHÔNG chứa giá trị gốc — no-PII trong log). */
  readonly kinds: readonly PiiKind[];
  /** Số lần thay thế (đếm, không giá trị). */
  readonly count: number;
}

/**
 * Email — chuẩn thực dụng (đủ bắt dạng phổ biến, không đòi RFC đầy đủ). Vá review R (security LOW): phần tên/miền nhận CHỮ CÓ DẤU
 * («nguyễn.văn@gmail.com» từng ra «nguyễn.vă[EMAIL]», «a@bücher.vn» lọt) và MỘT khoảng trắng quanh «@» («abc @ gmail.com»). Đuôi miền phải
 * là CHỮ ≥ 2 ⇒ «@» đứng một mình trong câu thường («họp @ 7h», «50k @ 1.5 suất») không bị nhận.
 */
// 🔴 Vá review lần 2 (security MEDIUM, ReDoS): chặn trên mọi lượng từ (tên ≤64 · nhãn miền ≤63 · ≤8 nhãn · đuôi ≤24 — giới hạn RFC) ⇒ mỗi vị trí
// bắt đầu tốn hằng số bước ⇒ tuyến tính; bản vá đầu bậc hai: 20.000 ký tự «ố…ố @ ố…ố» ≈ 5,9 s. Vá lần 3 (L-1): KHÔNG biên trái / biên phải —
// biên trái làm tên dài > 64 / dính tiền tố dài lọt nguyên («…lienhe@gmail.com»), biên phải làm «abc@gmail.com2» lọt; cạo phần khớp là đủ.
const EMAIL_RE =
  /[\p{L}\p{M}\p{N}._%+-]{1,64}[ \t]?@[ \t]?[\p{L}\p{M}\p{N}-]{1,63}(?:\.[\p{L}\p{M}\p{N}-]{1,63}){0,8}\.\p{L}{2,24}/gu;

/**
 * SĐT Việt Nam (dạng NỘI ĐỊA / "84" trần): 0xxxxxxxxx (10 số) / 0xxxxxxxxxx (11 số) / 84xxxxxxxxx, cho phép dấu cách/chấm/gạch xen giữa.
 * Đây là THÂN mẫu SĐT — dùng ở hai nơi: (1) phép thử "cả CỤM SỐ là một SĐT" (`PHONE_WHOLE_RE`, neo hai đầu) và (2) lưới đỡ cuối
 * (`PHONE_FALLBACK_RE`) cho SĐT viết trộn dấu mà bộ tách cụm không ghép được ("0901.234 567").
 */
const PHONE_BODY = String.raw`(?:84|0)(?:[\s.\-\u2013]?\d){8,10}`;
const PHONE_WHOLE_RE = new RegExp(String.raw`^${PHONE_BODY}$`, "u");

/**
 * 🔴 PII-GROUPED-ACCOUNT — SĐT có TIỀN TỐ QUỐC TẾ: "+84912345678" · "+84 912 345 678" · "+84-912-345-678" · "(+84) 912 345 678" ·
 * "(84) 912 345 678" · "+84 (0) 912 345 678". Trước 15/10 hai dạng có ngoặc lọt nguyên tới LLM. Chạy TRƯỚC bộ tách cụm: tiền tố
 * "(+84)" không phải chữ số, bộ tách cụm sẽ thấy "912 345 678" như một cụm trơ và gắn nhầm `[STK]`.
 */
const PHONE_INTL_RE = /(?:\(\s*\+?\s*84\s*\)|\+\s*84(?:\s*\(\s*0\s*\))?)(?:[\s.-]*\d)(?:[\s.-]?\d){7,9}(?!\d)/gu;

/**
 * 🔴 Vá review R (security MEDIUM) — mã vùng trong NGOẶC đi CÙNG tiền tố quốc tế: «+84 (28) 3822 1234» (ngoặc sau +84) · «(+84 28) 3822 1234» ·
 * «(84 90) 123 4567» (mã vùng trong ngoặc của +84). `PHONE_INTL_RE` đòi chữ số ngay sau «+84» / «)» ⇒ cả hai dạng lọt. Tổng chữ số quốc
 * gia (mã vùng bỏ «0» đầu + phần sau) 9–10 (luật ở `replaceNumbers`).
 */
const PHONE_INTL_AREA_RE =
  /(?:\+\s*84\s*\(\s*0?(\d{1,3})\s*\)|\(\s*\+?\s*84[\s.-]+0?(\d{1,3})\s*\))[\s.\-\u2013]*(\d(?:[\s.\-\u2013]?\d){5,9})(?!\d)/gu;
const PHONE_INTL_NATIONAL_MIN_DIGITS = 9;
const PHONE_INTL_NATIONAL_MAX_DIGITS = 10;

/** Số chữ số của một SĐT Việt Nam viết nội địa: di động 10 · cố định 11 (mã vùng 3 chữ số kể cả "0") — hai mẫu dưới đòi tổng trong khoảng này. */
const PHONE_DOMESTIC_MIN_DIGITS = 10;
const PHONE_DOMESTIC_MAX_DIGITS = 11;
const isDomesticPhoneLength = (digits: number): boolean => digits >= PHONE_DOMESTIC_MIN_DIGITS && digits <= PHONE_DOMESTIC_MAX_DIGITS;

/**
 * 🔴 AI-HARDEN A5 — MÃ VÙNG / đầu số trong NGOẶC: "(028) 3822 1234" · "(090) 123 4567" · "(0236)3821234". Trước 20/10 lọt nguyên: bộ tách cụm
 * thấy "028" riêng (ngoặc không phải dấu nối) và "3822 1234" là cụm 8 chữ số — dưới ngưỡng STK. Ngoặc chỉ nhận "0" + 1–3 chữ số ("(0)", "(12)"
 * không phải mã vùng); tổng 10–11 chữ số; đơn vị tiền theo sau ⇒ không (luật ở `replaceNumbers`).
 */
const PHONE_PAREN_AREA_RE = /\(\s*(0\d{1,3})\s*\)[\s.\-\u2013]*(\d(?:[\s.\-\u2013]?\d){5,8})(?!\d)/gu;

/**
 * 🔴 AI-HARDEN A5 — SĐT nối bằng "_" hoặc "/" ("0912_345_678" · "0912/345/678"). MỘT loại dấu cho cả số, đầu số "0"/"84" + 2–3 chữ số (ngày
 * "05/10/2026" chỉ có MỘT chữ số sau "0" ⇒ không khớp), tổng 10–11 chữ số. Tách khỏi `PHONE_BODY` có chủ đích: "/" trong thân mẫu chung sẽ
 * nhận "05/10/2026 08" là một SĐT.
 */
const PHONE_ALT_JOINER_RE = /(?<![\d/_.])(?:84|0)\d{2,3}([_/])\d{2,4}(?:\1\d{2,4}){1,2}(?![\d/_])/gu;

/**
 * 🔴 AI-HARDEN A5 — chữ số cách TỪNG chữ một ("stk 1 9 0 3 4 5 6 7 8 9"). Bộ tách cụm đòi nhóm nối ≥2 chữ số ⇒ mười "nhóm" 1 chữ số lọt
 * nguyên. Từ 6 chữ số; luật ở `spacedDigitsVerdict` (ngưỡng của luật 2b — không ngưỡng thứ hai).
 */
const SPACED_DIGITS_RE = /(?<![\d\p{L}])\d(?:[\p{Zs}\t\-\u2013]\d){5,}(?![\d\p{L}])/gu;

/**
 * Lưới đỡ cuối cho SĐT nội địa mà bộ tách cụm không ghép (dấu trộn: "0901.234 567"). BIÊN CHẶT hai đầu: không bắt khi kề **chữ số** hoặc
 * **dấu phân cách + chữ số** — tức không bao giờ cắt MỘT MẢNH của một cụm số dài hơn (lỗi cũ: "số tk 0071 0003 99999" ⇒ "0071 [SĐT]").
 * Dấu phẩy/chấm CUỐI CÂU ("…gọi 0901.234 567, mai…") KHÔNG ngăn cạo.
 */
const PHONE_FALLBACK_RE = new RegExp(String.raw`(?<!\d)(?<!\d[\s.,\-\u2013])${PHONE_BODY}(?!\d)(?![\s.,\-\u2013]?\d)`, "gu");

/**
 * 🔴 PII-GROUPED-ACCOUNT — CỤM SỐ: các nhóm chữ số nối bằng MỘT loại dấu — chỉ chấm ("0011.004.123456", "1.500.000") · chỉ phẩy
 * ("1,500,000") · cách/gạch ("1903 4567 8901 23", "1903-4567-8901-23", "0071 0003 99999") — hoặc một dãy chữ số liền.
 *  - Nhóm đầu 1–8 chữ số, nhóm nối 2–8 chữ số. Nhóm nối dính CHỮ ("500k", "3A", "2tr") KHÔNG thuộc cụm — đó là số tiền / số đếm
 *    đứng cạnh, không phải mảnh của STK ("0901 234 567 500k" ⇒ cụm "0901 234 567").
 *  - Một loại dấu cho cả cụm ⇒ khoảng tiền "1.500.000-2.000.000" là HAI cụm tiền, không phải một cụm 14 chữ số; và nối cách/gạch
 *    KHÔNG nhận nhóm là đầu của một số có chấm/phẩy ("hạn 15-10-2026 15.000.000" ⇒ ngày + tiền, không phải "[STK].000.000").
 */
const CLUSTER_GROUP = String.raw`\d{2,8}(?![\d\p{L}])`;
/**
 * Dấu nối cụm KHÔNG phải chấm/phẩy: MỌI dấu cách Unicode `\p{Zs}` (cách · NBSP · thin U+2009 · narrow NBSP U+202F · ideographic U+3000…)
 * · TAB · gạch nối · gạch en "–" (review #1411 LOW-3; PII-SCRUB-ROUND-3 LOW-3: trước đây chỉ cách + NBSP ⇒ "stk 1903\u20094567…" lọt).
 */
const CLUSTER_SPACE_JOINER = String.raw`[\p{Zs}\t\u2013-]`;
const CLUSTER_RE = new RegExp(
  [
    String.raw`(?<!\d)(?:`,
    String.raw`\d{1,8}(?:\.${CLUSTER_GROUP})+`,
    String.raw`|\d{1,8}(?:,${CLUSTER_GROUP})+`,
    String.raw`|\d{1,8}(?:${CLUSTER_SPACE_JOINER}${CLUSTER_GROUP}(?![.,]\d))+`,
    String.raw`|\d+)`,
  ].join(""),
  "gu",
);

/**
 * 🔴 PII-SCRUB-ROUND-3 LOW-3 — cụm nối bằng "/" ("stk 1903/4567/8901/23"). TÁCH khỏi `CLUSTER_RE` có chủ đích: "/" là dấu của NGÀY
 * ("15/10/2026") và PHÂN SỐ ("1/2 quỹ") ⇒ chỉ là STK khi CÓ ngữ cảnh (luật ở `isSlashAccount`); không ngữ cảnh ⇒ để nguyên cho
 * `CLUSTER_RE` xét TỪNG mảnh như cũ (một dãy liền ≥8 chữ số trong đó vẫn bị cạo — không mảnh nào được "che" bởi dấu "/").
 */
const SLASH_CLUSTER_RE = /(?<![\d/])\d{2,8}(?:\/\d{2,8})+(?![\d/])/gu;

/** Đơn vị tiền đứng NGAY SAU cụm ("500 nghìn", "500 000 đồng") — cụm đó là TIỀN, không phải STK. */
const MONEY_UNIT_AFTER_RE = /^\s*(?:k|nghìn|ngàn|nghin|ngan|tr|triệu|trieu|củ|tỷ|tỉ|đ|đồng|dong|vnd|vnđ)(?!\p{L})/iu;

/**
 * Ngữ cảnh STK đứng TRƯỚC cụm (cùng mệnh đề, cách ≤4 từ đệm: "số tài khoản của Minh là …", "STK: …", "Vietcombank …"):
 *  - `number`  — từ nói đích danh SỐ tài khoản: stk · số tk · số tài khoản · a/c · acc · account;
 *  - `account` — "tk" / "tài khoản" trần (có thể nói về tài khoản CÓ BAO NHIÊU tiền: "chuyển tk 2.500.000");
 *  - `bank`    — tên ngân hàng / ví phổ biến ở VN.
 */
type AccountContext = "number" | "account" | "bank";

/**
 * Số chữ số tối thiểu của một STK khi CÓ ngữ cảnh — bằng độ dài tối thiểu của miền STK hợp lệ của chính hệ thống
 * (`ACCOUNT_NUMBER_MIN_LENGTH` ở `@goppy/contracts`; domain không phụ thuộc contracts nên khớp hai hằng được khoá bằng test ở
 * `apps/api/.../base-llm-client.pii-cost-timeout.spec.ts` — US-INFRA-0843: lưới và miền giá trị không được nói hai điều khác nhau).
 */
export const PII_ACCOUNT_CONTEXT_MIN_DIGITS = 6;
/** Dãy LIỀN không có ngữ cảnh MẠNH (`number`) — kể cả sau "tk"/"tài khoản"/tên ngân hàng — là STK từ 8 chữ số ("tk 1500000" là TIỀN). */
const PII_PLAIN_RUN_MIN_DIGITS = 8;
/** Cụm NHIỀU nhóm không có ngữ cảnh là STK từ 9 chữ số (SĐT nhóm 10 số đã được nhận trước ở `PHONE_WHOLE_RE`). */
const PII_PLAIN_CLUSTER_MIN_DIGITS = 9;
/**
 * 🔴 PII-SCRUB-ROUND-3 MEDIUM-1 (security #1411 vòng 2) — dạng NGHÌN nối bằng DẤU CÁCH, KHÔNG đơn vị tiền theo sau ("190 345 678 901"):
 *  - có ngữ cảnh "tk"/"tài khoản"/tên ngân hàng ⇒ STK từ 9 chữ số ("tk 123 456 789");
 *  - không ngữ cảnh ⇒ STK từ 10 chữ số — tức ≥ 1 TỶ đồng nếu là tiền: quá lớn cho quỹ nhóm phi chính thức (ban phụ huynh, CLB…) mà lại
 *    viết KHÔNG đơn vị và bằng dấu cách; STK 10–15 chữ số thì rất hay viết như vậy.
 * Chỉ áp cho DẤU CÁCH: nghìn bằng chấm/phẩy ("1.200.000.000") là cách viết tiền phổ biến nhất ⇒ giữ (lỗ đã biết ở docblock đầu file).
 * GIÁ đã chấp nhận: "chuyển tk 150 000 000" / "quỹ có 1 200 000 000" (không đơn vị) ⇒ LLM thấy [STK]; viết kèm "đồng"/"đ"/"tr"… thì giữ.
 */
const PII_SPACED_THOUSANDS_CONTEXT_MIN_DIGITS = 9;
const PII_SPACED_THOUSANDS_PLAIN_MIN_DIGITS = 10;
/** Cụm nối "/" sau ngữ cảnh tk/tài khoản/ngân hàng là STK từ 9 chữ số — hơn một ngày đủ "dd/mm/yyyy" (8 chữ số: "vcb 15/10/2026" giữ). */
const PII_SLASH_CONTEXT_MIN_DIGITS = 9;
/** Cửa sổ ký tự ngay trước cụm số mà phép tìm ngữ cảnh STK đọc (đủ cho cụm từ ngữ cảnh + `CONTEXT_MAX_FILLER` từ đệm). */
const CONTEXT_WINDOW_CHARS = 80;
/** Phần lẻ của một số thập phân viết bằng "," / "." ("9007199254,75 triệu") — hàng xóm qua dấu là 1–2 chữ số. */
const DECIMAL_PART_MAX_DIGITS = 2;
/** Dãy liền ngắn hơn ngần này chữ số đứng sát "," / "." luôn coi là mảnh của một số (không bao giờ là STK trần). */
const FRAGMENT_MAX_DIGITS = 7;

const NUMBER_CONTEXT_WORDS: ReadonlyArray<readonly string[]> = [
  ["stk"], ["sotk"], ["số", "tk"], ["so", "tk"], ["số", "tài", "khoản"], ["so", "tai", "khoan"], ["a/c"], ["acc"], ["acct"], ["account"],
  // Vá review R (security LOW): số GIẤY TỜ TUỲ THÂN cũng là định danh — «cmnd 123 456 789» từng lọt như tiền. Placeholder vẫn là [STK] (một
  // nhãn «số định danh» cho LLM; không thêm loại PII mới vào audit).
  ["cmnd"], ["cccd"], ["cmt"], ["căn", "cước"], ["can", "cuoc"],
];
const ACCOUNT_CONTEXT_WORDS: ReadonlyArray<readonly string[]> = [["tk"], ["tài", "khoản"], ["tai", "khoan"]];
const BANK_WORDS: ReadonlySet<string> = new Set([
  "vcb", "vietcombank", "tcb", "techcombank", "mb", "mbbank", "acb", "bidv", "vietinbank", "vietin", "ctg", "agribank", "tpbank", "tpb",
  "vpbank", "vpb", "sacombank", "stb", "vib", "shb", "hdbank", "hdb", "ocb", "msb", "seabank", "eximbank", "lpbank", "namabank",
  "bacabank", "abbank", "pvcombank", "ncb", "vietbank", "bvbank", "kienlongbank", "scb", "momo", "zalopay", "viettelpay", "vnpay",
  "cake", "timo", "bank", "ngân", "hàng", "ngan", "hang",
]);
/** Từ đệm được phép giữa từ ngữ cảnh và cụm số (từ viết HOA đầu — tên người/ngân hàng — cũng được bỏ qua). */
const CONTEXT_FILLER_WORDS: ReadonlySet<string> = new Set([
  "số", "so", "là", "la", "của", "cua", "chủ", "tên", "ten", "anh",
  "chị", "em", "bé", "mình", "tôi", "ở", "tại", "nhận", "mới", "cũ",
]);
const CONTEXT_MAX_FILLER = 4;

function endsWithWords(words: readonly string[], at: number, phrase: readonly string[]): boolean {
  if (at + 1 < phrase.length) return false;
  return phrase.every((w, i) => words[at + 1 - phrase.length + i] === w);
}

/** Ngữ cảnh mạnh nhất tìm được ngay trước cụm số (dừng ở chữ số / dấu ngắt mệnh đề / từ lạ). */
function accountContextBefore(before: string): AccountContext | null {
  const tail = before.slice(-CONTEXT_WINDOW_CHARS);
  const cut = Math.max(...[...",;!?\n"].map((c) => tail.lastIndexOf(c)), tail.search(/\d(?!.*\d)/su));
  const raw = tail.slice(cut + 1).normalize("NFC").split(/\s+/u).map((w) => w.replace(/^[:().\-–]+|[:().\-–]+$/gu, "")).filter((w) => w !== "");
  const words = raw.map((w) => w.toLowerCase());
  let found: AccountContext | null = null;
  let filler = 0;
  for (let i = words.length - 1; i >= 0 && filler <= CONTEXT_MAX_FILLER; i -= 1) {
    if (NUMBER_CONTEXT_WORDS.some((p) => endsWithWords(words, i, p))) return "number";
    if (ACCOUNT_CONTEXT_WORDS.some((p) => endsWithWords(words, i, p))) return "account";
    if (BANK_WORDS.has(words[i]!)) found = "bank";
    else if (!CONTEXT_FILLER_WORDS.has(words[i]!) && !/^\p{Lu}/u.test(raw[i]!)) return found;
    filler += 1;
  }
  return found;
}

/**
 * Dạng TIỀN của một cụm nhiều nhóm: nhóm đầu 1–3 chữ số không mở bằng 0, MỌI nhóm sau đúng 3 chữ số, nối bằng chấm · phẩy · dấu cách
 * ("1.500.000", "15.000.000", "1,500,000", "150 000 000", "500 000 đồng"). "0011.004.123456" (nhóm 4·3·6, mở bằng 0) và
 * "1903 4567 8901 23" (nhóm 4) KHÔNG phải dạng tiền.
 * 🔴 Review #1411 MEDIUM: trước đây nối bằng dấu cách phải có đơn vị theo sau ⇒ "thu 150 000 000", "vcb 2 000 000" bị cạo thành [STK] ⇒
 * LLM + `money-fallback` mất số tiền. Ngữ cảnh MẠNH ("stk 123 456 789") đã được xét TRƯỚC phép này ở `classifyCluster` (luật 2).
 */
function isMoneyShaped(cluster: string): boolean {
  return moneyShapeSeparator(cluster) !== null;
}

/** Dấu nghìn của một cụm dạng TIỀN (`isMoneyShaped`): "." · "," · " " (MỌI dấu cách Unicode quy về " "); không phải dạng tiền ⇒ null. */
function moneyShapeSeparator(cluster: string): "." | "," | " " | null {
  const unified = cluster.replace(/\p{Zs}/gu, " ");
  if (!/^[\d., ]+$/u.test(unified)) return null;
  const seps = new Set(unified.replace(/\d/gu, ""));
  if (seps.size !== 1) return null;
  const groups = unified.split(/[., ]/u);
  if (groups.length < 2) return null;
  const shaped = /^[1-9]\d{0,2}$/u.test(groups[0]!) && groups.slice(1).every((g) => /^\d{3}$/u.test(g));
  return shaped ? ([...seps][0] as "." | "," | " ") : null;
}

/**
 * Dãy LIỀN là MẢNH của một số có phần lẻ / phân nhóm mà bộ tách cụm không ghép ("9007199254,75 triệu") — không coi là STK trần.
 * 🔴 Review #1411 MEDIUM (security): bản trước coi MỌI dãy kề "<chữ số>,"/",<chữ số>" là mảnh ⇒ STK trong danh sách phân cách bằng phẩy
 * ("Nguyen Van A,0912345678,1903456789") lọt nguyên. Nay chỉ khi hàng xóm qua dấu là phần lẻ 1–2 chữ số, hoặc chính dãy ngắn (≤7 chữ số).
 * 🔴 PII-SCRUB-ROUND-3 MEDIUM-2: số 1–2 chữ số kề dấu phẩy còn là cột STT / cột số nhỏ của CSV ("1,1903456789,Nguyen Van A" ·
 * "1903456789,12,Nguyen Van A") ⇒ với dãy DÀI chỉ coi là thập phân khi NGAY SAU phần lẻ là đơn vị tiền ("9007199254,75 triệu").
 */
function isNumberFragment(digits: number, before: string, after: string): boolean {
  if (digits <= FRAGMENT_MAX_DIGITS) return /^[.,]\d/u.test(after) || /\d[.,]$/u.test(before);
  const decimalAfter = new RegExp(String.raw`^[.,]\d{1,${DECIMAL_PART_MAX_DIGITS}}(?!\d)`, "u").exec(after);
  if (decimalAfter !== null && MONEY_UNIT_AFTER_RE.test(after.slice(decimalAfter[0].length))) return true;
  const decimalBefore = new RegExp(String.raw`(?<![\d.,])\d{1,${DECIMAL_PART_MAX_DIGITS}}[.,]$`, "u");
  return decimalBefore.test(before) && MONEY_UNIT_AFTER_RE.test(after);
}

type ClusterVerdict = "phone" | "account" | "keep";

/**
 * 🔴 PII-GROUPED-ACCOUNT — PHÂN LOẠI MỘT CỤM SỐ (luật duy nhất; bảng ca hai chiều ở `pii-scrubber.test.ts`):
 *  1. cả cụm là SĐT (`PHONE_WHOLE_RE`) ⇒ `phone`;
 *  2. ngữ cảnh `number` (stk/số tk/số tài khoản/a/c) ⇒ ≥6 chữ số là `account`, KỂ CẢ dạng nghìn ("stk 123.456.789") — trừ khi có đơn vị tiền;
 *  2b. nghìn bằng DẤU CÁCH không đơn vị ⇒ `account` từ 9 chữ số khi có ngữ cảnh tk/ngân hàng, từ 10 chữ số khi không (PII-SCRUB-ROUND-3);
 *  3. dạng TIỀN (`isMoneyShaped` — nghìn bằng chấm/phẩy/cách) ⇒ `keep`;
 *  4. ngữ cảnh `account` (tk/tài khoản) hoặc `bank` ⇒ cụm NHIỀU nhóm ≥6 chữ số là `account`; dãy LIỀN vẫn theo ngưỡng 8 của luật 5
 *     ("chuyển tk 2500000", "ck vcb 150000" là TIỀN — review #1411 HIGH);
 *  5. không ngữ cảnh ⇒ dãy liền ≥8 chữ số, hoặc cụm nhiều nhóm tổng ≥9 chữ số, là `account`;
 *  6. cụm nhiều nhóm không đạt mà nhóm CUỐI có đơn vị tiền theo sau ("0901 234 567 500 nghìn") ⇒ bỏ nhóm cuối (nó là tiền), xét lại phần đầu.
 * Còn lại ⇒ `keep` (ngày "15.10.2026", năm học "2025-2026", số đếm nhỏ).
 */
function classifyCluster(cluster: string, before: string, after: string): { verdict: ClusterVerdict; length: number } {
  const whole = { length: cluster.length };
  if (PHONE_WHOLE_RE.test(cluster)) return { verdict: "phone", ...whole };
  const digits = cluster.replace(/\D/gu, "").length;
  const multi = /\D/u.test(cluster);
  const ctx = accountContextBefore(before);
  const unitAfter = MONEY_UNIT_AFTER_RE.test(after);
  if (ctx === "number" && digits >= PII_ACCOUNT_CONTEXT_MIN_DIGITS && !unitAfter) return { verdict: "account", ...whole };
  const spacedMin = ctx === null ? PII_SPACED_THOUSANDS_PLAIN_MIN_DIGITS : PII_SPACED_THOUSANDS_CONTEXT_MIN_DIGITS;
  if (moneyShapeSeparator(cluster) === " " && !unitAfter && digits >= spacedMin) return { verdict: "account", ...whole };
  const fragment = !multi && isNumberFragment(digits, before, after);
  if (!isMoneyShaped(cluster) && !fragment) {
    const contextual = ctx === "number" || (multi && (ctx === "account" || ctx === "bank"));
    const threshold = contextual ? PII_ACCOUNT_CONTEXT_MIN_DIGITS : multi ? PII_PLAIN_CLUSTER_MIN_DIGITS : PII_PLAIN_RUN_MIN_DIGITS;
    if (digits >= threshold && !(multi && unitAfter)) return { verdict: "account", ...whole };
    if (multi && unitAfter) {
      const head = cluster.replace(/[^\d][\d]+$/u, "");
      const sub = classifyCluster(head, before, cluster.slice(head.length) + after);
      if (sub.verdict !== "keep") return sub;
    }
  }
  return { verdict: "keep", ...whole };
}

/**
 * Cụm nối "/" là STK khi CÓ ngữ cảnh: `number` (stk/số tk…) từ 6 chữ số như luật 2; tk/tài khoản/ngân hàng từ 9 chữ số; có đơn vị tiền
 * theo sau hoặc không ngữ cảnh ⇒ không (ngày, phân số, "lớp 12/2025").
 */
function isSlashAccount(cluster: string, before: string, after: string): boolean {
  const ctx = accountContextBefore(before);
  if (ctx === null || MONEY_UNIT_AFTER_RE.test(after)) return false;
  const digits = cluster.replace(/\D/gu, "").length;
  return digits >= (ctx === "number" ? PII_ACCOUNT_CONTEXT_MIN_DIGITS : PII_SLASH_CONTEXT_MIN_DIGITS);
}

/**
 * Dãy chữ số cách TỪNG chữ một (`SPACED_DIGITS_RE`): cả dãy là SĐT (`PHONE_WHOLE_RE`) ⇒ `[SĐT]`; STK theo CÙNG ngưỡng của nghìn bằng dấu cách
 * (luật 2b) — vì "các suất 1 2 3 4 5 6 7 8" là một danh sách, không phải STK: `number` ≥6 · tk/ngân hàng ≥9 · không ngữ cảnh ≥10; đơn vị tiền
 * theo sau ⇒ giữ.
 */
function spacedDigitsVerdict(m: string, before: string, after: string, phone: () => string, account: (() => string) | null): string {
  const digits = m.replace(/\D/gu, "");
  if (PHONE_WHOLE_RE.test(digits)) return phone();
  if (account === null || MONEY_UNIT_AFTER_RE.test(after)) return m;
  const ctx = accountContextBefore(before);
  const min = ctx === "number" ? PII_ACCOUNT_CONTEXT_MIN_DIGITS : ctx === null ? PII_SPACED_THOUSANDS_PLAIN_MIN_DIGITS : PII_SPACED_THOUSANDS_CONTEXT_MIN_DIGITS;
  return digits.length >= min ? account() : m;
}

/** Một lượt thay số: SĐT (luôn) và STK (khi `account` khác null). Hai cửa công khai dưới đây gọi chung ⇒ MỘT định nghĩa. */
function replaceNumbers(text: string, phone: () => string, account: (() => string) | null): string {
  const intlOnly = text
    .replace(PHONE_INTL_AREA_RE, (m: string, a1: string | undefined, a2: string | undefined, rest: string) => {
      const n = (a1 ?? a2 ?? "").length + rest.replace(/\D/gu, "").length;
      return n >= PHONE_INTL_NATIONAL_MIN_DIGITS && n <= PHONE_INTL_NATIONAL_MAX_DIGITS ? phone() : m;
    })
    .replace(PHONE_INTL_RE, phone)
    .replace(PHONE_PAREN_AREA_RE, (m: string, area: string, rest: string, offset: number, all: string) =>
      isDomesticPhoneLength(area.length + rest.replace(/\D/gu, "").length) && !MONEY_UNIT_AFTER_RE.test(all.slice(offset + m.length)) ? phone() : m,
    )
    .replace(PHONE_ALT_JOINER_RE, (m: string) => (isDomesticPhoneLength(m.replace(/\D/gu, "").length) ? phone() : m))
    .replace(SPACED_DIGITS_RE, (m: string, offset: number, all: string) => spacedDigitsVerdict(m, all.slice(0, offset), all.slice(offset + m.length), phone, account));
  const intl =
    account === null
      ? intlOnly
      : intlOnly.replace(SLASH_CLUSTER_RE, (cluster: string, offset: number, all: string) =>
          isSlashAccount(cluster, all.slice(0, offset), all.slice(offset + cluster.length)) ? account() : cluster,
        );
  const clustered = intl.replace(CLUSTER_RE, (cluster: string, offset: number, all: string) => {
    const { verdict, length } = classifyCluster(cluster, all.slice(0, offset), all.slice(offset + cluster.length));
    const rest = cluster.slice(length);
    if (verdict === "phone") return phone() + rest;
    if (verdict === "account" && account !== null) return account() + rest;
    return cluster;
  });
  return clustered.replace(PHONE_FALLBACK_RE, phone);
}

/**
 * 🔴 US-AIV2-0112 H04 (review #1038 HIGH-1 · @UC0162) — thay mọi SỐ ĐIỆN THOẠI (đúng định nghĩa SĐT của bộ cạo PII: 0/84/+84/(+84)/+84 (0),
 * dấu cách/chấm/gạch xen giữa) bằng `[SĐT]`. Bộ đọc tiền gọi hàm này TRƯỚC khi tách số: "0912 345 678" từng ra 912 đ, "+84912345678" ra
 * 84.912.345.678 đ — tự tin. MỘT định nghĩa SĐT cho cả hai việc (cạo PII · không coi là tiền); không bản chép regex thứ hai. STK không bị
 * đụng ở đây (cụm STK giữ nguyên — không còn bị cắt nửa thành "0071 [SĐT]").
 */
export function maskPhoneNumbers(text: string): string {
  return replaceNumbers(text, () => PII_PLACEHOLDER.PHONE, null);
}

/**
 * Ký tự TOÀN KHỔ có thể dựng PII — NFKC đưa về ASCII: chữ số ("１９０３"), chữ Latin, "＠" (+ "﹫" nhỏ), "．", "＿", "－", "＋" (AI-HARDEN A5:
 * "a＠gmail.com" từng lọt nguyên — `EMAIL_RE` chỉ biết "@" ASCII). Dấu câu toàn khổ khác ("（", "，"…) KHÔNG đổi.
 */
const FULLWIDTH_PII_CHAR_RE = /[\uFF10-\uFF19\uFF21-\uFF3A\uFF41-\uFF5A\uFF20\uFE6B\uFF0E\uFF3F\uFF0D\uFF0B]/gu;
/** Ký tự định dạng vô hình (`\p{Cf}` — zero-width space/joiner, BOM…) đứng SÁT một chữ số. */
const FORMAT_CHAR_BY_DIGIT_RE = /\p{Cf}+(?=\d)|(?<=\d)\p{Cf}+/gu;

/**
 * 🔴 Review #1411 LOW-3 (security) — trước khi cạo: chữ số / ký tự PII fullwidth ⇒ ASCII (NFKC trên ĐÚNG ký tự đó) và bỏ ký tự định dạng vô hình sát chữ số
 * ("1903\u200B4567…" từng lọt vì bộ tách cụm thấy ba dãy 4 chữ số). CHỈ cho chuỗi gửi LLM. KHÔNG NFKC cả câu: NFKC đổi cả "…" ⇒ "...",
 * "²" ⇒ "2"… ⇒ đổi câu gửi LLM của những lượt không có PII nào (và băm prompt của bộ eval) — chữ tiếng Việt có dấu không đổi (NFKC = NFC).
 */
function normalizeDigitsForScrub(text: string): string {
  return text.replace(FULLWIDTH_PII_CHAR_RE, (d) => d.normalize("NFKC")).replace(FORMAT_CHAR_BY_DIGIT_RE, "");
}

/**
 * 🔴 @SEC-FAIL-01 — CẠO PII trước khi gửi sang LLM bên thứ ba. Gọi ở CỔNG DUY NHẤT (LlmClient) ⇒
 * không có đường nào lách.
 */
export function scrubPiiForLlm(text: string): ScrubResult {
  const kinds = new Set<PiiKind>();
  let count = 0;
  const hit = (kind: PiiKind, placeholder: string) => () => {
    kinds.add(kind);
    count += 1;
    return placeholder;
  };

  // Thứ tự QUAN TRỌNG: email trước (chứa chữ+số) → SĐT quốc tế (tiền tố không phải chữ số) → từng CỤM SỐ phân loại NGUYÊN CỤM
  // (SĐT · STK · giữ) → lưới đỡ SĐT dấu trộn. Cả cụm được xét một lần ⇒ không bao giờ ra nửa-SĐT-nửa-STK.
  const out = replaceNumbers(
    normalizeDigitsForScrub(text).replace(EMAIL_RE, hit("email", PII_PLACEHOLDER.EMAIL)),
    hit("phone", PII_PLACEHOLDER.PHONE),
    hit("account", PII_PLACEHOLDER.ACCOUNT),
  );

  return Object.freeze({
    text: out,
    redacted: count > 0,
    kinds: Object.freeze([...kinds]),
    count,
  });
}

/** true nếu văn bản CÒN chứa PII thô (dùng cho assertion/gate — "không được rò ra ngoài"). */
export function containsPii(text: string): boolean {
  return scrubPiiForLlm(text).redacted;
}
